import { type Expense } from "@finway-group/shared/lib/models"
import { type VendorPaymentProposal } from "@finway-group/shared/lib/models/vendorPayment/vendorPayment.interface"
import { useQueries, useQuery } from "@tanstack/react-query"
import chunk from "lodash/chunk"
import { queryClient } from "~Shared/config/reactQueryConfig"
import { CacheEnum } from "~Shared/queries/consts"
import { AuthzService } from "~Shared/services"
import { ExpenseHttpService } from "~Shared/services/expense.http.service"
import { VendorPaymentService } from "~Shared/services/vendorPayment.service"
import { RefetchTableTypes } from "~Shared/store/actions/tables/tableTypes.enum"
import { TablesEnum } from "~Shared/store/reducers/tableConfigReducer"
import { type RootState } from "~Shared/store/rootState.interface"

// the ids travel in the query string, which the server caps at a few kilobytes
const EXPENSE_IDS_PER_REQUEST = 100

const fetchExpensesByIds = async (ids: Array<string>, signal?: AbortSignal) => {
    const expensesPerChunk = await Promise.all(
        chunk(ids, EXPENSE_IDS_PER_REQUEST).map(async (idsOfChunk) => {
            const { expenses } = await ExpenseHttpService.fetchExpenses(`&_id[in]=${idsOfChunk.join(",")}`, "", 1, idsOfChunk.length, { signal })
            return expenses
        }),
    )
    return expensesPerChunk.flat()
}

const getProposalExpensesQuery = ({ invoices, creditNotes }: VendorPaymentProposal) => {
    const ids = [...invoices, ...creditNotes].map(({ expense }) => expense)
    return {
        queryKey: [CacheEnum.VENDOR_PAYMENT_PROPOSAL_EXPENSES, ids],
        queryFn: ({ signal }: { signal?: AbortSignal }): Promise<Array<Expense>> => fetchExpensesByIds(ids, signal),
    }
}

export const useVendorPaymentProposalsQuery = () =>
    useQuery({
        queryKey: [CacheEnum.VENDOR_PAYMENT_PROPOSALS],
        queryFn: VendorPaymentService.fetchProposals,
        enabled: AuthzService.canLoggedInUserSeeVendorPayments(),
        refetchOnMount: "always",
    })

export const useVendorPaymentProposalExpensesQueries = (proposals: Array<VendorPaymentProposal>) => useQueries({ queries: proposals.map(getProposalExpensesQuery) })

export const useVendorPaymentProposalExpensesQuery = (proposal: VendorPaymentProposal) => useQuery(getProposalExpensesQuery(proposal))

export const useVendorPaymentQuery = (id?: string) =>
    useQuery({
        queryKey: [CacheEnum.VENDOR_PAYMENT, id],
        queryFn: () => VendorPaymentService.fetchVendorPayment(String(id)),
        enabled: Boolean(id) && AuthzService.canLoggedInUserSeeVendorPayments(),
    })

export const invalidateVendorPaymentProposals = () =>
    Promise.all([
        queryClient.invalidateQueries({ queryKey: [CacheEnum.VENDOR_PAYMENT_PROPOSALS] }),
        queryClient.invalidateQueries({ queryKey: [CacheEnum.VENDOR_PAYMENT_PROPOSAL_EXPENSES] }),
    ])

export const invalidateVendorPayment = (id: string) => queryClient.invalidateQueries({ queryKey: [CacheEnum.VENDOR_PAYMENT, id] })

/** Paging, sorting or searching "to be paid" doesn't come through here, only changes of the data it shows. */
export const isToBePaidRefetchAction = ({ type, payload }: { type: string; payload?: unknown }) =>
    type === RefetchTableTypes.REFETCH_TABLE_PAY_AND_EXPORT_TO_BE_PAID ||
    (typeof payload === "object" && payload !== null && "tables" in payload && Array.isArray(payload.tables) && payload.tables.includes(TablesEnum.PAY_AND_EXPORT_TO_BE_PAID))

/** @returns the vendor payment the expense was part of before the update, as held in the store */
export const getPreviousVendorPayment = ({ expenses }: Pick<RootState, "expenses">, expenseId: string) =>
    (expenses.item?.id === expenseId ? expenses.item : expenses.items.find(({ id }) => id === expenseId))?.vendorPayment
